
import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronUp, ChevronDown, Info, Download, Upload, Wifi, WifiOff, AlertTriangle } from 'lucide-react';
import { getNetworkStatus, isLowDataMode } from '@/utils/networkMonitor';
import { useDiagnosticMode } from '@/hooks/useDiagnosticMode';

interface PerformanceMetric {
  name: string;
  value: string | number;
  timestamp: number;
}

interface ResourceTiming {
  url: string;
  size: number;
  duration: number;
  initiatorType: string;
}

export function NetworkDiagnostics() {
  const { isDiagnosticMode, toggleDiagnosticMode } = useDiagnosticMode();
  const [isExpanded, setIsExpanded] = useState(false);
  const [networkInfo, setNetworkInfo] = useState(getNetworkStatus());
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetric[]>([]);
  const [resourceStats, setResourceStats] = useState({
    totalRequests: 0,
    totalSize: 0,
    slowestRequest: { url: '', time: 0 }
  });
  const [resourceTimings, setResourceTimings] = useState<ResourceTiming[]>([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [unusualConditions, setUnusualConditions] = useState<string[]>([]);
  const updateInterval = useRef<number | null>(null);
  const captureInterval = useRef<number | null>(null);
  
  // Only run the component logic if diagnostic mode is enabled
  useEffect(() => {
    if (!isDiagnosticMode) return;
    
    // Set up network status monitoring
    const monitorNetwork = () => {
      const newNetworkInfo = getNetworkStatus();
      setNetworkInfo(newNetworkInfo);
      
      // Check for unusual conditions
      detectUnusualConditions(newNetworkInfo);
    };
    
    // Initial check
    monitorNetwork();
    
    // Regular updates
    updateInterval.current = window.setInterval(monitorNetwork, 5000);
    
    // Collect performance metrics for first load
    collectInitialPerformanceMetrics();
    
    // Set up performance observer for ongoing metrics
    setupPerformanceObservers();
    
    // Collect resource timing information
    collectResourceTimings();
    
    return () => {
      if (updateInterval.current) {
        clearInterval(updateInterval.current);
      }
      if (captureInterval.current) {
        clearInterval(captureInterval.current);
      }
    };
  }, [isDiagnosticMode]);
  
  // Re-collect resource timings when expanded to get the latest data
  useEffect(() => {
    if (isExpanded && isDiagnosticMode) {
      collectResourceTimings();
    }
  }, [isExpanded, isDiagnosticMode]);
  
  // Detection of unusual or contradictory network conditions
  const detectUnusualConditions = (networkInfo: any) => {
    const warnings: string[] = [];
    
    // Check for contradictory conditions
    if (networkInfo.effectiveType === 'slow-2g' && networkInfo.rtt && networkInfo.rtt < 300) {
      warnings.push('Network is classified as slow-2g but RTT is fast');
    }
    
    if (networkInfo.effectiveType === '4g' && networkInfo.rtt && networkInfo.rtt > 1000) {
      warnings.push('Network is classified as 4g but RTT is very high');
    }
    
    // Check for DOM completion vs network classification mismatch
    const domComplete = performanceMetrics.find(m => m.name === 'DOM Complete')?.value;
    if (domComplete !== undefined && typeof domComplete === 'number') {
      if (networkInfo.effectiveType === 'slow-2g' && domComplete < 1000) {
        warnings.push('Page loaded quickly but network is classified as slow-2g');
      }
      if (networkInfo.effectiveType === '4g' && domComplete > 5000) {
        warnings.push('Page loaded slowly despite 4g network classification');
      }
    }
    
    setUnusualConditions(warnings);
  };
  
  const collectInitialPerformanceMetrics = () => {
    if (!performance || !performance.timing) {
      return;
    }
    
    const timing = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (!timing) return;
    
    const now = Date.now();
    const newMetrics: PerformanceMetric[] = [
      { name: 'TTFB', value: Math.round(timing.responseStart), timestamp: now },
      { name: 'DOM Interactive', value: Math.round(timing.domInteractive), timestamp: now },
      { name: 'DOM Complete', value: Math.round(timing.domComplete), timestamp: now },
      { name: 'Load', value: Math.round(timing.loadEventEnd), timestamp: now }
    ];
    
    setPerformanceMetrics(prev => [...prev, ...newMetrics]);
  };
  
  const setupPerformanceObservers = () => {
    if (!('PerformanceObserver' in window)) return;
    
    try {
      // Observe LCP
      const lcpObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        setPerformanceMetrics(prev => [
          ...prev, 
          { 
            name: 'LCP', 
            value: Math.round(lastEntry.startTime), 
            timestamp: Date.now()
          }
        ]);
      });
      
      // Observe FID
      const fidObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1] as PerformanceEventTiming;
        
        if (lastEntry && 'processingStart' in lastEntry) {
          setPerformanceMetrics(prev => [
            ...prev, 
            { 
              name: 'FID', 
              value: Math.round(lastEntry.processingStart - lastEntry.startTime), 
              timestamp: Date.now()
            }
          ]);
        }
      });
      
      // Observe CLS
      const clsObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        let clsScore = 0;
        
        entries.forEach(entry => {
          // @ts-ignore - CLS entry value
          if (entry.value) clsScore += entry.value;
        });
        
        setPerformanceMetrics(prev => [
          ...prev.filter(metric => metric.name !== 'CLS'), 
          { 
            name: 'CLS', 
            value: clsScore.toFixed(3), 
            timestamp: Date.now()
          }
        ]);
      });
      
      // Start observations
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      fidObserver.observe({ type: 'first-input', buffered: true });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
      
      return () => {
        lcpObserver.disconnect();
        fidObserver.disconnect();
        clsObserver.disconnect();
      };
    } catch (e) {
      console.error('[Diagnostics] Error setting up performance observers:', e);
    }
  };
  
  const collectResourceTimings = () => {
    try {
      const resources = performance.getEntriesByType('resource');
      
      let totalSize = 0;
      let slowestTime = 0;
      let slowestUrl = '';
      const timings: ResourceTiming[] = [];
      
      resources.forEach(resource => {
        const res = resource as PerformanceResourceTiming;
        const size = res.transferSize || 0;
        const time = res.duration;
        
        totalSize += size;
        
        if (time > slowestTime) {
          slowestTime = time;
          slowestUrl = res.name;
        }
        
        // Only track resources that took more than 50ms to load
        if (time > 50) {
          timings.push({
            url: res.name.split('/').pop() || res.name,
            size: Math.round(size / 1024 * 10) / 10, // KB with 1 decimal
            duration: Math.round(time),
            initiatorType: res.initiatorType
          });
        }
      });
      
      // Sort by duration (slowest first)
      timings.sort((a, b) => b.duration - a.duration);
      
      setResourceTimings(timings.slice(0, 5)); // Keep only the 5 slowest
      
      setResourceStats({
        totalRequests: resources.length,
        totalSize: Math.round(totalSize / 1024), // KB
        slowestRequest: {
          url: slowestUrl.split('/').pop() || slowestUrl,
          time: Math.round(slowestTime)
        }
      });
    } catch (e) {
      console.error('[Diagnostics] Error collecting resource timings:', e);
    }
  };
  
  const toggleCapture = () => {
    if (isCapturing) {
      // Stop capturing
      if (captureInterval.current) {
        clearInterval(captureInterval.current);
        captureInterval.current = null;
      }
    } else {
      // Start capturing
      collectResourceTimings();
      captureInterval.current = window.setInterval(() => {
        collectResourceTimings();
      }, 3000);
    }
    
    setIsCapturing(!isCapturing);
  };
  
  const shareData = () => {
    try {
      // Format diagnostics data
      const diagnosticsData = {
        timestamp: new Date().toISOString(),
        networkStatus: networkInfo,
        performanceMetrics: performanceMetrics,
        resourceStats: resourceStats,
        resourceDetails: resourceTimings,
        warnings: unusualConditions,
        userAgent: navigator.userAgent
      };
      
      // Create a text version
      const textData = JSON.stringify(diagnosticsData, null, 2);
      
      // Create a blob and create a download link
      const blob = new Blob([textData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `network-diagnostics-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
      
      alert('Diagnostics data downloaded.');
    } catch (e) {
      console.error('[Diagnostics] Error sharing data:', e);
      alert('Failed to share diagnostics data.');
    }
  };
  
  if (!isDiagnosticMode) {
    return null;
  }
  
  // Format connection type display
  const getConnectionDisplay = () => {
    if (!networkInfo.online) return 'Offline';
    
    // Map effectiveType to more readable formats
    const typeMap: Record<string, string> = {
      'slow-2g': '2G (Poor)',
      '2g': '2G',
      '3g': '3G',
      '4g': '4G'
    };
    
    return typeMap[networkInfo.effectiveType] || networkInfo.effectiveType;
  };
  
  // Get metric color based on value and name
  const getMetricColor = (name: string, value: string | number) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    switch (name) {
      case 'LCP':
        return numValue < 2500 ? 'text-green-500' : numValue < 4000 ? 'text-yellow-500' : 'text-red-500';
      case 'FID':
        return numValue < 100 ? 'text-green-500' : numValue < 300 ? 'text-yellow-500' : 'text-red-500';
      case 'CLS':
        return numValue < 0.1 ? 'text-green-500' : numValue < 0.25 ? 'text-yellow-500' : 'text-red-500';
      case 'TTFB':
        return numValue < 800 ? 'text-green-500' : numValue < 1800 ? 'text-yellow-500' : 'text-red-500';
      case 'DOM Complete':
      case 'Load':
        return numValue < 1000 ? 'text-green-500' : numValue < 3000 ? 'text-yellow-500' : 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };
  
  // Get connection status color
  const getConnectionColor = () => {
    if (!networkInfo.online) return 'text-red-500';
    
    switch (networkInfo.effectiveType) {
      case 'slow-2g':
      case '2g':
        return 'text-red-500';
      case '3g':
        return 'text-yellow-500';
      case '4g':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };
  
  return (
    <div 
      className="fixed bottom-4 right-4 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden"
      style={{ 
        maxWidth: isExpanded ? '380px' : '250px', 
        opacity: 0.95, 
        fontSize: '12px' 
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-2 bg-gray-100 dark:bg-gray-700">
        <div className="flex items-center space-x-2">
          {networkInfo.online ? 
            <Wifi size={14} className={getConnectionColor()} /> : 
            <WifiOff size={14} className="text-red-500" />
          }
          <span className="font-medium">Network Diagnostics</span>
        </div>
        <div className="flex items-center space-x-1">
          <button 
            onClick={() => setIsExpanded(!isExpanded)} 
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            {isExpanded ? 
              <ChevronDown size={14} /> : 
              <ChevronUp size={14} />
            }
          </button>
          <button 
            onClick={toggleDiagnosticMode} 
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            <X size={14} />
          </button>
        </div>
      </div>
      
      {/* Unusual Conditions Warnings */}
      {unusualConditions.length > 0 && (
        <div className="p-2 bg-amber-50 border-b border-amber-200 dark:bg-amber-900 dark:border-amber-800">
          <div className="flex items-center space-x-1 text-amber-700 dark:text-amber-300">
            <AlertTriangle size={12} />
            <span className="font-medium">Detected Issues:</span>
          </div>
          <ul className="mt-1 text-xs text-amber-600 dark:text-amber-300 pl-4">
            {unusualConditions.map((warning, index) => (
              <li key={index} className="list-disc text-xs">{warning}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Connection Status */}
      <div className="p-2 border-b dark:border-gray-600">
        <div className="flex justify-between">
          <span className="text-gray-500">Connection:</span>
          <span className={`font-medium ${getConnectionColor()}`}>
            {getConnectionDisplay()}
          </span>
        </div>
        {networkInfo.downlink !== null && (
          <div className="flex justify-between">
            <span className="text-gray-500">Downlink:</span>
            <span className="font-medium">
              {networkInfo.downlink.toFixed(1)} Mbps
            </span>
          </div>
        )}
        {networkInfo.rtt !== null && (
          <div className="flex justify-between">
            <span className="text-gray-500">RTT:</span>
            <span className="font-medium">
              {Math.round(networkInfo.rtt)} ms
            </span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-500">Low Data Mode:</span>
          <span className="font-medium">
            {isLowDataMode() ? 'Yes' : 'No'}
          </span>
        </div>
      </div>
      
      {/* Performance Metrics (only when expanded) */}
      {isExpanded && (
        <>
          <div className="p-2 border-b dark:border-gray-600">
            <div className="font-medium mb-1">Performance Metrics</div>
            {performanceMetrics
              .filter((metric, index, self) => 
                index === self.findIndex(m => m.name === metric.name)
              )
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(metric => (
                <div key={metric.name} className="flex justify-between text-xs">
                  <span className="text-gray-500">{metric.name}:</span>
                  <span className={`font-medium ${getMetricColor(metric.name, metric.value)}`}>
                    {typeof metric.value === 'number' ? `${metric.value}ms` : metric.value}
                  </span>
                </div>
              ))
            }
          </div>
          
          {/* Resource Stats */}
          <div className="p-2 border-b dark:border-gray-600">
            <div className="font-medium mb-1">Resources</div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Requests:</span>
              <span className="font-medium">{resourceStats.totalRequests}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Total Size:</span>
              <span className="font-medium">{resourceStats.totalSize} KB</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Slowest:</span>
              <span className="font-medium truncate max-w-36" title={resourceStats.slowestRequest.url}>
                {resourceStats.slowestRequest.url} ({resourceStats.slowestRequest.time}ms)
              </span>
            </div>
          </div>
          
          {/* Slowest Resources */}
          {resourceTimings.length > 0 && (
            <div className="p-2 border-b dark:border-gray-600">
              <div className="font-medium mb-1">Slowest Resources</div>
              <div className="text-xs space-y-1 max-h-28 overflow-y-auto">
                {resourceTimings.map((resource, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <div className="truncate max-w-36" title={resource.url}>
                      <span className={resource.duration > 2000 ? 'text-red-500' : 'text-gray-500'}>
                        {resource.initiatorType}: {resource.url}
                      </span>
                    </div>
                    <div className="flex space-x-2 whitespace-nowrap">
                      <span className="text-gray-600">{resource.size}KB</span>
                      <span className={resource.duration > 2000 ? 'text-red-500' : 'text-gray-500'}>
                        {resource.duration}ms
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Actions */}
      <div className="p-2 flex justify-between">
        <button 
          onClick={toggleCapture}
          className={`text-xs px-2 py-1 rounded ${isCapturing ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'} hover:bg-opacity-80`}
        >
          {isCapturing ? 'Stop Capture' : 'Capture'}
        </button>
        <button 
          onClick={shareData}
          className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-opacity-80"
        >
          Download Report
        </button>
      </div>
    </div>
  );
}
